import React, { useState, useRef, useEffect } from 'react';
import { X, Play, Volume2, VolumeX } from 'lucide-react';

// Playlist: videos play one after another, automatically looping back to the first.
// `alwaysMuted: true` forces that clip to have no audio regardless of the mute toggle.
const PLAYLIST = [
  {
    url: "https://customer-assets.emergentagent.com/job_6155f013-7394-403e-8ef1-e61234cc182e/artifacts/u2q8wmhe_video_1781750017043.mp4",
    alwaysMuted: false
  },
  {
    url: "https://customer-assets.emergentagent.com/job_6155f013-7394-403e-8ef1-e61234cc182e/artifacts/xb4jpr5b_1783149281212_0_edited-video-vedit-8ce16a24-efe8-4676-9218-dc454f6ce628.mp4",
    alwaysMuted: true
  }
];

const VideoPopup = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const videoRef = useRef(null);

  const currentClip = PLAYLIST[currentIndex];
  const effectiveMuted = isMuted || currentClip.alwaysMuted;

  useEffect(() => {
    if (isOpen && videoRef.current) {
      videoRef.current.muted = effectiveMuted;
      videoRef.current.play().catch(() => {});
    }
  }, [isOpen, currentIndex, effectiveMuted]);

  const handleEnded = () => {
    setCurrentIndex((prev) => (prev + 1) % PLAYLIST.length);
  };

  const toggleMute = () => {
    setIsMuted((prev) => !prev);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        data-testid="video-popup-reopen-btn"
        className="fixed bottom-5 right-5 z-[9998] w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 text-white shadow-2xl flex items-center justify-center hover:scale-110 transition-transform animate-pulse"
        aria-label="Play video"
      >
        <Play className="w-6 h-6 ml-0.5" />
      </button>
    );
  }

  return (
    <div
      data-testid="video-popup"
      className="fixed bottom-5 right-5 z-[9998] w-64 sm:w-72 rounded-2xl overflow-hidden shadow-2xl border-2 border-yellow-400 bg-black"
    >
      <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-yellow-400 to-pink-500">
        <span className="text-sm font-bold text-gray-900 truncate">Ambarsariya</span>
        <div className="flex items-center gap-1">
          <button
            onClick={toggleMute}
            data-testid="video-popup-mute-btn"
            className="w-7 h-7 rounded-full bg-white/30 hover:bg-white/50 flex items-center justify-center text-gray-900 transition-colors"
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {effectiveMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            data-testid="video-popup-close-btn"
            className="w-7 h-7 rounded-full bg-white/30 hover:bg-white/50 flex items-center justify-center text-gray-900 transition-colors"
            aria-label="Close video"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <video
        ref={videoRef}
        key={currentClip.url}
        src={currentClip.url}
        className="w-full h-auto block"
        autoPlay
        playsInline
        onEnded={handleEnded}
        data-testid="video-popup-player"
      />
    </div>
  );
};

export default VideoPopup;
